import { clearState, loadState, saveState } from "shared/localstorage_helpers"

import type { DIYComposerState } from "./interfaces"

// The slice of composer state that describes the Applet the user is building. Everything else
// (services, permissions, preview, errors) is loaded again when the page loads.
export type AppletDraft = Pick<
  DIYComposerState,
  "selectedTrigger" | "selectedQueries" | "selectedActions" | "filterCode" | "delay" | "appletFeatures" | "appletTier"
>

interface StoredDraft {
  version: number
  savedAt: number
  applet: AppletDraft
}

// Bump when the shape of AppletDraft changes, so old drafts are dropped instead of restored wrong.
const DRAFT_VERSION = 1

// A draft older than this is stale. The user gets an empty composer instead.
export const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000

export const draftKey = (userId?: string) => `diy_composer_draft:${userId}`

const hasContent = (applet: AppletDraft) =>
  !!applet.selectedTrigger ||
  applet.selectedActions?.length > 0 ||
  applet.selectedQueries?.length > 0 ||
  !!applet.filterCode

export const clearDraft = (userId?: string) => clearState(draftKey(userId))

// The only keys ever written or restored. Reading through this rather than returning the
// stored object matters: loadDraft's result is spread into SET_OVERRIDE_STATE, so an entry
// carrying extra keys -- localStorage is writable by anything on this origin, and by other
// builds of this composer -- would otherwise overwrite live composer state such as `user`
// or `services`.
const pickDraft = (source: AppletDraft | DIYComposerState): AppletDraft => ({
  selectedTrigger: source.selectedTrigger,
  selectedQueries: source.selectedQueries,
  selectedActions: source.selectedActions,
  filterCode: source.filterCode,
  delay: source.delay,
  appletFeatures: source.appletFeatures,
  appletTier: source.appletTier,
})

export const saveDraft = (state: DIYComposerState, userId?: string) => {
  const applet = pickDraft(state)

  if (!hasContent(applet)) return clearDraft(userId)

  const draft: StoredDraft = {
    version: DRAFT_VERSION,
    savedAt: Date.now(),
    applet,
  }

  saveState(draft, draftKey(userId))
}

// A step has to at least name itself and its service for InitialStep to render it.
const isStep = (step: unknown) =>
  !!step && typeof step === "object" && typeof (step as { name?: unknown }).name === "string"

// Check the shape before it reaches composer state rather than trusting that we wrote it.
const isWellFormed = (applet?: AppletDraft) =>
  !!applet &&
  typeof applet === "object" &&
  Array.isArray(applet.selectedQueries) &&
  Array.isArray(applet.selectedActions) &&
  Array.isArray(applet.appletFeatures) &&
  applet.selectedQueries.every(isStep) &&
  applet.selectedActions.every(isStep) &&
  (!applet.selectedTrigger || isStep(applet.selectedTrigger)) &&
  (applet.filterCode === undefined || typeof applet.filterCode === "string") &&
  (applet.delay === undefined || typeof applet.delay === "number")

export const loadDraft = (userId?: string): AppletDraft | undefined => {
  const draft: StoredDraft | undefined = loadState(draftKey(userId))

  // loadState returns undefined both for "no entry" and for "entry did not parse". Only the
  // second leaves something behind worth removing.
  if (!draft) {
    if (localStorage.getItem(draftKey(userId)) !== null) clearDraft(userId)
    return undefined
  }

  // Anything we will not restore gets removed, so a bad entry cannot sit in storage being
  // re-read and re-rejected on every page load. savedAt is checked for finiteness first:
  // Date.now() - undefined is NaN, and every comparison against NaN is false, so a missing
  // timestamp would otherwise slip past the age check.
  const unusable =
    draft.version !== DRAFT_VERSION ||
    !Number.isFinite(draft.savedAt) ||
    Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS ||
    !isWellFormed(draft.applet) ||
    !hasContent(draft.applet)

  if (unusable) {
    clearDraft(userId)
    return undefined
  }

  return pickDraft(draft.applet)
}
